import { createBrowserRouter, RouterProvider, Outlet } from "react-router-dom";
import { Provider } from 'react-redux';
import { lazy, Suspense, useEffect } from "react";
import NotFoundPage from "./pages/special/NotFoundPage";
import store from './store/store';
import './i18n';
import { useTranslation } from "react-i18next";
import { getIndexData, getFaqData, getMinecraftData, getMBData, getPrivacyPolicyData, getTermsOfServiceData } from "./api/api";

const Index = lazy(() => import("./pages/Index").then(m => ({ default: m.Index })));
const Faq = lazy(() => import("./pages/Faq").then(m => ({ default: m.Faq })));
const Minecraft = lazy(() => import("./pages/Minecraft").then(m => ({ default: m.Minecraft })));
const MB = lazy(() => import("./pages/MB").then(m => ({ default: m.MB })));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));

const dataCache = {};

const makeLoader = (key, fetcher) => async () => {
  if (dataCache[key]) return dataCache[key];
  try {
    const data = (await fetcher()).data;
    dataCache[key] = data;
    return data;
  } catch (e) { console.error(e); return null; }
};

const indexLoader = makeLoader('index', getIndexData);
const faqLoader = makeLoader('faq', getFaqData);
const minecraftLoader = makeLoader('minecraft', getMinecraftData);
const mbLoader = makeLoader('mb', getMBData);
const privacyLoader = makeLoader('privacy', getPrivacyPolicyData);
const termsLoader = makeLoader('terms', getTermsOfServiceData);

function preloadAll() {
  import("./pages/Index");
  import("./pages/Faq");
  import("./pages/Minecraft");
  import("./pages/MB");
  if (!dataCache.index) getIndexData().then(r => { dataCache.index = r.data; }).catch(() => {});
  if (!dataCache.faq) getFaqData().then(r => { dataCache.faq = r.data; }).catch(() => {});
  if (!dataCache.minecraft) getMinecraftData().then(r => { dataCache.minecraft = r.data; }).catch(() => {});
  if (!dataCache.mb) getMBData().then(r => { dataCache.mb = r.data; }).catch(() => {});
}

function RootLayout() {
  const { i18n } = useTranslation();

  useEffect(() => {
    const langMap = { ua: 'uk', en: 'en' };
    document.documentElement.lang = langMap[i18n.language] || i18n.language;
  }, [i18n.language]);

  useEffect(() => {
    if ('requestIdleCallback' in window) {
      const id = requestIdleCallback(preloadAll);
      return () => cancelIdleCallback(id);
    }
    const id = setTimeout(preloadAll, 2000);
    return () => clearTimeout(id);
  }, []);

  return (
    <Suspense fallback={null}>
      <Outlet />
    </Suspense>
  );
}

const router = createBrowserRouter([
  {
    element: <RootLayout />,
    children: [
      { path: "/", id: "index", loader: indexLoader, element: <Index />, errorElement: <NotFoundPage /> },
      { path: "/faq", id: "faq", loader: faqLoader, element: <Faq />, errorElement: <NotFoundPage /> },
      { path: "/minecraft", id: "minecraft", loader: minecraftLoader, element: <Minecraft />, errorElement: <NotFoundPage /> },
      { path: "/mount-and-blade", id: "mb", loader: mbLoader, element: <MB />, errorElement: <NotFoundPage /> },
      { path: "/privacy-policy", id: "privacy", loader: privacyLoader, element: <PrivacyPolicy />, errorElement: <NotFoundPage /> },
      { path: "/terms-of-service", id: "terms", loader: termsLoader, element: <TermsOfService />, errorElement: <NotFoundPage /> },
    ],
  },
]);

export const AppRouter = () => (
  <Provider store={store}>
    <RouterProvider router={router} />
  </Provider>
);
